// Story 2.4 — query hook for GET /api/v1/admin/drops (the admin
// drop-state cockpit list). Filters: kind, status, from, to. URL-synced
// via the page component using `useSearchParams`.
//
// staleTime is shorter (15s) than Story 2.3's drop-for-portfolio query
// (30s) because the cockpit needs to reflect recent transitions
// promptly when the admin is working through a batch.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type DropStatus = "scheduled" | "live" | "retiring" | "retired";
export type DropKind = "regular" | "special" | "welcome";

export type AdminDropsFilters = {
  kind: DropKind | null;
  status: DropStatus | null;
  from: string | null; // ISO-8601 RFC3339
  to: string | null;
  page: number;
  page_size: number;
};

export const EMPTY_DROPS_FILTERS: AdminDropsFilters = {
  kind: null,
  status: null,
  from: null,
  to: null,
  page: 1,
  page_size: 50,
};

export type AdminDropRow = {
  drop_id: string;
  portfolio_id: string;
  portfolio_title: string;
  kind: string;
  status: string;
  price_cents: number;
  per_user_cap: number | null;
  scheduled_at: string;
  live_at: string | null;
  retiring_at: string | null;
  retired_at: string | null;
  is_perpetual: boolean;
  sold_count: number;
  /// Subset of sold_count: how many minted editions were free weekly
  /// claims (acquired_via = 'welcome_grant'). Always <= sold_count.
  free_claim_count: number;
  linked_bulletin_post_id: string | null;
  linked_bulletin_slug: string | null;
  linked_bulletin_title: string | null;
  created_at: string;
};

export type AdminDropsListResponse = {
  data: {
    items: AdminDropRow[];
    page: number;
    page_size: number;
    total: number;
  };
};

function buildQueryString(filters: AdminDropsFilters): string {
  const params = new URLSearchParams();
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.status) params.set("status", filters.status);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.page !== 1) params.set("page", String(filters.page));
  if (filters.page_size !== 50) params.set("page_size", String(filters.page_size));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

async function fetchAdminDropsList(
  filters: AdminDropsFilters,
): Promise<AdminDropsListResponse> {
  const response = await fetch(
    `/api/v1/admin/drops${buildQueryString(filters)}`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const raw = await response.json();
      if (raw?.error?.code) message = raw.error.code;
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminDropsListResponse;
}

export function useAdminDropsList(filters: AdminDropsFilters) {
  return useQuery({
    queryKey: ["admin-drops-list", filters],
    queryFn: () => fetchAdminDropsList(filters),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/// Read filters from a URLSearchParams instance (used by the page on
/// mount and on history navigation). Defaults match EMPTY_DROPS_FILTERS.
export function filtersFromSearchParams(
  searchParams: URLSearchParams,
): AdminDropsFilters {
  const rawKind = searchParams.get("kind");
  const rawStatus = searchParams.get("status");
  const rawPage = searchParams.get("page");
  const rawPageSize = searchParams.get("page_size");
  return {
    kind:
      rawKind === "regular" || rawKind === "special" || rawKind === "welcome"
        ? rawKind
        : null,
    status:
      rawStatus === "scheduled" ||
      rawStatus === "live" ||
      rawStatus === "retiring" ||
      rawStatus === "retired"
        ? rawStatus
        : null,
    from: searchParams.get("from"),
    to: searchParams.get("to"),
    page: rawPage ? Math.max(1, parseInt(rawPage, 10)) || 1 : 1,
    page_size: rawPageSize ? Math.min(200, parseInt(rawPageSize, 10) || 50) : 50,
  };
}

/// Build a URLSearchParams instance from filters (omitting defaults so
/// the URL stays compact). Used when writing filters back via
/// `setSearchParams(searchParamsFromFilters(next))`.
export function searchParamsFromFilters(
  filters: AdminDropsFilters,
): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.status) params.set("status", filters.status);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.page !== 1) params.set("page", String(filters.page));
  if (filters.page_size !== 50)
    params.set("page_size", String(filters.page_size));
  return params;
}
